/**
 * Product recommendations for guide sections.
 * Uses grass-aware mappings so inline picks stay relevant for each guide.
 */

import { amazonUrl, amazonSearchUrl } from '@/lib/affiliate'

import { amazonImageUrl as amazonImg } from '@/lib/amazon-image'

export interface ProductRecommendation {
  name: string
  description: string
  category: string
  affiliateUrl: string
  price: string
  productsPageFilter?: string
  imageUrl?: string // Amazon product image URL
}

export type ProductKey =
  | 'fertilizer-pgf-complete'
  | 'fertilizer-lesco-24-0-11'
  | 'establish-zoysia-plugs'
  | 'establish-st-augustine-plugs'
  | 'establish-buffalo-plugs'
  | 'fertilizer-simple-1648'
  | 'fertilizer-southern-lawn-food'
  | 'fertilizer-milorganite'
  | 'fertilizer-neighbors-envy'
  | 'fertilizer-ammonium-sulfate'
  | 'fertilizer-lilly-miller-161616'
  | 'fertilizer-liquid-nitrogen'
  | 'starter-scotts'
  | 'pre-emergent-barricade'
  | 'pre-emergent-scotts-halts'
  | 'pre-emergent-dimension'
  | 'post-emergent-trimec'
  | 'post-emergent-atrazine-southern'
  | 'post-emergent-image-southern'
  | 'post-emergent-speedzone'
  | 'post-emergent-tzone'
  | 'post-emergent-tenacity'
  | 'post-emergent-sedgehammer'
  | 'seed-bermuda'
  | 'seed-annual-ryegrass'
  | 'seed-cool-pennington'
  | 'seed-fescue-jonathan-green'
  | 'seed-zoysia-zenith'
  | 'seed-centipede-pennington'
  | 'seed-bahia-pennington'
  | 'seed-buffalo-outsidepride'
  | 'seed-nightshift-ryegrass'
  | 'seed-nightshift-fescue'
  | 'seed-nightshift-kbg'
  | 'grub-control-grubex'
  | 'pest-sevin'
  | 'tool-spreader-edgeguard-mini'
  | 'tool-hose-end-sprayer'
  | 'tool-fiskars-weed-puller'
  | 'surfactant-non-ionic'
  | 'soil-lime-pennington'
  | 'soil-compost-coast-of-maine'
  | 'soil-magical-alkaline'
  | 'soil-humic-acid'
  | 'soil-sea-kelp'
  | 'fungicide-diseaseex'
  | 'iron-southern-ag'
  | 'iron-petratools'
  | 'summer-hydretain'
  | 'summer-wetting-agent'
  | 'smart-rachio'
  | 'smart-soil-sensor'
  // Added 2026-06-30 for /compare brand pages (see data/comparisons.tsx brand entries).
  // These are the generic-tier products the compare sides name; PAAPI-verified titles.
  | 'weed-feed-scotts'
  | 'weed-triple-action-scotts'
  | 'post-emergent-quinclorac'
  | 'seed-scotts-sun-shade'
  | 'fertilizer-scotts-turf-builder'
  | 'soil-peat-moss'
  | 'soil-topsoil'

const PRODUCTS: Record<ProductKey, ProductRecommendation> = {
  'fertilizer-pgf-complete': {
    name: 'The Andersons PGF Complete 16-4-8',
    description: 'Balanced slow-release fertilizer for established lawns that need steady feeding without a quick flush of top growth.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B07SYLCKRH'),
    price: '$60',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41xelE+7eJL'),
  },
  'fertilizer-lesco-24-0-11': {
    name: 'Lesco Professional Turf Fertilizer 24-0-11 (50 lb)',
    description: 'Commercial-grade Lesco professional turf fertilizer, 24-0-11, covering ~13,500 sq ft per 50-lb bag. Sold on Amazon by SiteOne Landscape Supply, so homeowners without a nearby SiteOne store can still buy the pro-grade product.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B07876FR4M'),
    price: '$88',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41opU+aZG8L'),
  },
  'fertilizer-simple-1648': {
    name: 'Simple Lawn Solutions Advanced 16-4-8',
    description: 'Liquid 16-4-8 lawn food for quick feeding on established lawns when you want balanced NPK and simple hose-end application.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B06XWFL84R'),
    price: '$24',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('81rO6rxh0PL'),
  },
  'fertilizer-southern-lawn-food': {
    name: 'Scotts Turf Builder Southern Lawn Food',
    description: 'Warm-season fertilizer built for southern lawns, especially when you want a homeowner-friendly granular option.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B000UGU59K'),
    price: '$51',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('71qLT1YpEML'),
  },
  'fertilizer-milorganite': {
    // 2026-07-06: restored to the 32 lb bag (B0FGDNF55M, in stock $34.84) now that
    // a 32 lb listing is available again. The 5 lb (B000UOB1P4) was a stopgap after
    // the original 32 lb (B0DDHTJKCY) went OOS on 2026-06-24.
    name: 'Milorganite 6-4-0 (32 lb)',
    description: 'Lower-nitrogen, slow-release option that is a better fit for centipede and other lawns that burn easily with aggressive feeding.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B0FGDNF55M'),
    price: '$35',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('51+RWV7fs3L'),
  },
  'starter-scotts': {
    name: 'Scotts Turf Builder Starter Food for New Grass',
    description: 'Starter lawn food for seeding, overseeding, or new sod when the guide calls for a planting-time phosphorus boost.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B00DK49DVA'),
    price: '$35',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('81oY9zpw+qL'),
  },
  'pre-emergent-barricade': {
    name: 'Barricade 4FL Herbicide Concentrate (4 Fl Oz)',
    description: 'Prodiamine pre-emergent concentrate for established lawns when the guide specifically calls for long-residual crabgrass prevention. The 4 oz bottle treats roughly half an acre at the standard label rate.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B00OXD2CKG'),
    price: '$30',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('71Fl3q83ubL'),
  },
  'pre-emergent-scotts-halts': {
    name: 'Scotts Halts Crabgrass and Grassy Weed Preventer',
    description: 'Granular pre-emergent for homeowners who want a simple crabgrass-prevention product before soil temperatures hit the danger zone.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B08L6T3B4H'),
    price: '$56',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('5196bQGYE8L'),
  },
  'pre-emergent-dimension': {
    name: 'Dimension 2EW',
    description: 'Dithiopyr pre-emergent that also provides early post-emergent activity on young crabgrass escapes.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B0051GXXPW'),
    price: '$157',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('81HKCRiHb9L'),
  },
  'post-emergent-trimec': {
    name: 'Trimec Classic',
    description: 'Classic three-way broadleaf herbicide for tolerant lawns when you need dependable control of clover, dandelion, plantain, and chickweed.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B015BSWOV8'),
    price: '$87',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('41UgRCjJAFL'),
  },
  'post-emergent-atrazine-southern': {
    name: 'Southern Ag 006130 Atrazine St. Augustine Weed Killer 32oz Specialty Herbicide',
    description: 'Southern-grass herbicide option for St. Augustine and centipede lawns where three-way broadleaf products are unsafe.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B0052MZBYQ'),
    price: '$16',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('41xJ0HypHcL'),
  },
  'post-emergent-image-southern': {
    name: 'IMAGE Southern Lawn Weed Killer',
    description: 'Broadleaf weed killer labeled for several southern grasses, including St. Augustine, centipede, bahiagrass, buffalograss, and zoysia.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B079H2MRSN'),
    price: '$15',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('41rGQMFyBFL'),
  },
  'post-emergent-speedzone': {
    name: 'SpeedZone EW Lawn Weed Killer',
    description: 'Fast-acting lawn weed killer for cool-season lawns that targets dandelion, clover, spurge, plantain, and other listed weeds.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B001PCRKDC'),
    price: '$36',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('71C3BcfmB6S'),
  },
  'seed-bermuda': {
    // Enthusiast pick: Blackjack II (Maya), a named, fine-textured improved bermuda
    // cultivar — the carpet-like dark-green turf enthusiasts want — in a full-lawn 5 lb bag.
    name: 'Outsidepride Maya (Blackjack II) Bermuda Grass Seed (5 lb)',
    description: 'Named, fine-textured improved bermuda cultivar for dense, carpet-like, dark-green full-sun turf; 5 lb full-lawn bag.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0B1KY7D35'),
    price: '$45',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41dAOh7+zwL'),
  },
  'seed-annual-ryegrass': {
    // PAAPI-verified 2026-07-11: $42.46, in stock (the 10/20/50 lb Pennington
    // annual-rye listings were all out of stock at verification time).
    name: 'Pennington Annual Ryegrass Retail Bag to Overseed Warm Season Grasses, 25 lb',
    description: 'Fast-germinating annual rye for green winter color over dormant warm-season lawns; 25 lb covers about 5,000 sq ft of overseeding.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B01C3B61BK'),
    price: '$42',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41p6wr9OZnL'),
  },
  'seed-cool-pennington': {
    name: 'Pennington Smart Seed Sun and Shade Mix',
    description: 'Versatile cool-season blend for overseeding and repairs where a multi-variety mix makes more sense than a single species.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B08QCNV9ZV'),
    price: '$61',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('81NLk-gC4YS'),
  },
  'seed-fescue-jonathan-green': {
    // Fine-fescue key. Enthusiast pick: a true fine-fescue blend (hard, chewings,
    // creeping red), not a tall-fescue or all-purpose mix. 10 lb full-lawn bag.
    name: 'Outsidepride Legacy Fine Fescue Blend (10 lb)',
    description: 'Elite fine-fescue blend (hard, chewings, creeping red) for soft, shade-tolerant, low-input lawns; 10 lb for full coverage.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B004MN5NO4'),
    price: '$70',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('51SOMbhBklL'),
  },
  'seed-zoysia-zenith': {
    // Enthusiast pick: authentic Zenith zoysia, 100% pure seed from Patten Seed (the
    // breeder) — the credible seeded-zoysia cultivar. Zoysia seeds at a low rate and
    // is costly per lb, so 2 lb is a real full-lawn bag for this species.
    name: 'Zenith Zoysia Grass Seed by Patten Seed (2 lb, 100% pure seed)',
    description: 'The genuine Zenith zoysia cultivar from the breeder — the credible way to establish a zoysia lawn from seed rather than sod or plugs.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B006RSDM3Q'),
    price: '$200',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('61okbOuZayL'),
  },
  'establish-zoysia-plugs': {
    // Zoysia is best established from plugs, not seed (seeded zoysia is a slow, costly
    // specialty product). These 50 EZPlug plugs are the credible, affordable entry point
    // for a homeowner starting a zoysia lawn or filling in from an existing one.
    name: 'Zoysia Grass Plugs (50 Large Plugs, EZPlug)',
    description: 'Fifty live zoysia plugs — the practical way to start or spread a zoysia lawn, since zoysia establishes far better from plugs than from seed.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B00NMNU098'),
    price: '$55',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41eegOgstBL'),
  },
  'establish-st-augustine-plugs': {
    // St. Augustine cannot be grown from seed — it is established from sod or plugs only.
    // Palmetto is the most common home-lawn cultivar and these plugs ship nationwide
    // (unlike some CitraBlue/ProVista listings restricted from CA/AZ).
    name: 'St. Augustine Palmetto Grass Plugs (18 Extra-Large Live Plugs)',
    description: 'Live Palmetto St. Augustine plugs — the way you actually start a St. Augustine lawn, since this grass is established from plugs or sod rather than seed.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0976DN86K'),
    price: '$60',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41P5nbHoovL'),
  },
  'establish-buffalo-plugs': {
    // Buffalograss-from-seed is a slow, costly niche product the guides steer away from;
    // plugs are the practical establishment route. UC Verde is the popular low-mow
    // ornamental-lawn cultivar sold as plugs.
    name: 'UC Verde Buffalograss Plugs',
    description: 'Live UC Verde buffalograss plugs — the practical way to start a low-water buffalograss lawn, since buffalo establishes more reliably from plugs than from its slow, pricey seed.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0792FPRJK'),
    price: '$70',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('61dRIUXx-pL'),
  },
  'seed-centipede-pennington': {
    // Enthusiast pick: TifBlair, the improved centipede cultivar, direct from the
    // farm. Centipede sows at a very low rate, so 1 lb covers ~4,000 sq ft — a real
    // full-lawn bag for this species despite the small weight.
    name: 'TifBlair Centipede Grass Seed (1 lb, covers up to ~4,000 sq ft)',
    description: 'The improved TifBlair centipede cultivar for acidic, low-input southeastern lawns; 1 lb covers a full lawn at centipede\'s low seeding rate.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B01834WDV8'),
    price: '$66',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('51UJ-pNXKOL'),
  },
  'seed-bahia-pennington': {
    // Enthusiast pick: Argentine bahia (finer-textured lawn type, vs coarse
    // Pensacola) from Hancock Seed, in a 10 lb full-lawn bag.
    name: 'Hancock Argentine Bahia Grass Seed (10 lb)',
    description: 'Finer-textured Argentine bahia for low-input, drought-tolerant southern lawns on sandy soils; 10 lb full-lawn bag.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B091RF9DVW'),
    price: '$74',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41HBBLfpb6S'),
  },
  'seed-buffalo-outsidepride': {
    // 2026-06-30: repointed from search URL to direct ASIN; product/anchor updated to match.
    name: 'Outsidepride Perennial Buffalo Grass Seed, 5 lb',
    description: 'Seeded buffalograss for low-water, full-sun lawns in prairie and western climates.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0B16RSMDT'),
    price: '$190',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('61xmo0mnQdL'),
  },
  'grub-control-grubex': {
    name: 'Scotts GrubEx1 Season Long Grub Killer, 5,000 sq. ft.',
    description: 'Preventive chlorantraniliprole grub control for late spring applications before root-feeding larvae explode.',
    category: 'Pest Control',
    affiliateUrl: amazonUrl('B0050DV4ZW'),
    price: '$29',
    productsPageFilter: 'Pest & Disease Control',
    imageUrl: amazonImg('41vLRIqeMiL'),
  },
  // Keyed 'pennington' for legacy reasons, but Pennington Fast Acting Lime isn't
  // reliably carried on Amazon — a search URL landed shoppers on a mixed results page.
  // Swapped to Espoma Lightning Lime, an equivalent fast-acting pelletized lime that IS
  // stocked, with a direct /dp/ link and matching name (honest anchor text, better
  // conversion than a search page).
  'soil-lime-pennington': {
    name: 'Espoma Organic Lightning Lime (Fast-Acting Pelletized Lime)',
    description: 'Fast-acting pelletized lime for low-pH lawns when a soil test confirms you need to raise pH before fertilizing harder. Covers up to 5,000 sq ft.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B00YMQX4VA'),
    price: '$38',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41Ea0OBw5WL'),
  },
  'soil-compost-coast-of-maine': {
    name: 'Coast of Maine Quoddy Blend Lobster Compost',
    description: 'Compost-based soil amendment for topdressing and improving low-organic-matter lawns after aeration or soil prep.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B07CM8RRVN'),
    price: '$13',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('4105k9TbzbL'),
  },
  'soil-magical-alkaline': {
    name: 'Jonathan Green Mag-I-Cal Plus for Lawns in Alkaline & Hard Soil',
    description: 'Lawn soil amendment for high-pH, compacted soils when you need to loosen the soil and help move pH in the right direction.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B07MPJCPRP'),
    price: '$31',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('51WM-+ak01L'),
  },
  'fungicide-diseaseex': {
    name: 'Scotts DiseaseEx Lawn Fungicide',
    description: 'Azoxystrobin-based homeowner fungicide for brown patch, dollar spot, and other common lawn disease pressure windows.',
    category: 'Disease Control',
    affiliateUrl: amazonUrl('B075R7BNXV'),
    price: '$21',
    productsPageFilter: 'Pest & Disease Control',
    imageUrl: amazonImg('710N1nUPNFL'),
  },
  'iron-southern-ag': {
    name: 'Southern Ag Chelated Liquid Iron',
    description: 'Chelated iron supplement for deeper green color without pushing a surge of nitrogen growth in hot weather.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B00A528VOK'),
    price: '$14',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41vAc2iKM1L'),
  },

  // --- YardBrah picks ---
  'fertilizer-neighbors-envy': {
    name: "Neighbor's Envy 7-in-1 Liquid Fertilizer",
    description: 'All-in-one liquid concentrate with nitrogen, iron, humic acid, and sea kelp. 64 oz covers up to 8,000 sq ft.',
    category: 'Fertilizer',
    // 2026-07-18: swapped from the OOS 2.5-gallon variant (B0F5Y4NVQD, 18 clicks
    // but persistently unavailable) to the in-stock 64 oz bottle (B0GYJLDD5J);
    // image/price/coverage below describe that exact variant. Price $39.99
    // verified via PetraTools (PAAPI reports in-stock but returns no price here).
    affiliateUrl: amazonUrl('B0GYJLDD5J'),
    price: '$40',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41zvES8yVvL'),
  },
  'fertilizer-ammonium-sulfate': {
    name: 'Dark Matter 21-0-0 Ammonium Sulfate',
    description: 'High-nitrogen, fast-acting small prill ammonium sulfate for quick green-up and iron supplementation. 40 lb bag.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B0DZ327Z13'),
    price: '$60',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('61G9WvmlO5L'),
  },
  'fertilizer-lilly-miller-161616': {
    name: 'Lilly Miller 16-16-16 All Purpose',
    description: 'Balanced triple-16 granular fertilizer for lawns and gardens when soil tests show broad nutrient needs. 20 lb bag.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B0C8FDHL35'),
    price: '$18',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('51kvMIXw+JL'),
  },
  'fertilizer-liquid-nitrogen': {
    name: 'PetraTools Liquid Nitrogen 28-0-0',
    description: 'High-nitrogen liquid spray for quick green-up. Slow-release 28-0-0 formula, 32 oz hose-end application.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B095BTZPYD'),
    price: '$30',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('81r6Hwbd69L'),
  },
  'iron-petratools': {
    name: 'PetraTools Liquid Iron for Lawns',
    description: 'Chelated liquid iron for deep green color without pushing nitrogen growth. EDTA-free, 32 oz concentrate.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B095VQCL85'),
    price: '$17',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('81CoCGsDkxL'),
  },
  'post-emergent-tzone': {
    name: 'T-Zone SE Broadleaf Herbicide',
    description: 'Pro-grade broadleaf herbicide for tough weeds including clover, ground ivy, wild violet, and oxalis. 1 quart.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B00NWSIHCK'),
    price: '$38',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('61Y+uzC4D+L'),
  },
  'post-emergent-tenacity': {
    name: 'Tenacity Turf Herbicide 8 oz',
    description: 'Mesotrione pre/post-emergent for crabgrass, nimblewill, and bentgrass. Safe on cool-season lawns.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B005DUTNF0'),
    price: '$62',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('7150ZVCDOfL'),
  },
  'post-emergent-sedgehammer': {
    name: 'Sedgehammer Plus Turf Solution 13.5grams',
    description: 'Halosulfuron sedge herbicide for yellow and purple nutsedge when standard broadleaf sprays do not work.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B007PHMAYK'),
    price: '$14',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('51w5G6PEFFL'),
  },
  'pest-sevin': {
    name: 'Sevin Insect Killer Granules 10 lb',
    description: 'Broad-spectrum granular insect killer for grubs, ants, fleas, ticks, and 100+ listed lawn pests.',
    category: 'Pest Control',
    affiliateUrl: amazonUrl('B07874K3H6'),
    price: '$11',
    productsPageFilter: 'Pest & Disease Control',
    imageUrl: amazonImg('81cb40ZM3qL'),
  },
  'tool-spreader-edgeguard-mini': {
    name: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
    description: 'Simple broadcast spreader for granular fungicides, grub control, fertilizer, and recovery products that need even coverage.',
    category: 'Tool',
    affiliateUrl: amazonUrl('B002YPS1KK'),
    price: '$53',
    productsPageFilter: 'Lawn Care Tools',
    imageUrl: amazonImg('31WEgJaq8FL'),
  },
  'tool-hose-end-sprayer': {
    name: 'Ortho Dial N Spray Hose-End Sprayer (14 Dilution Settings)',
    description: 'Adjustable metered hose-end sprayer for liquid herbicides, iron, wetting agents, and foliar recovery products.',
    category: 'Tool',
    affiliateUrl: amazonUrl('B0071D0EZK'),
    price: '$16',
    productsPageFilter: 'Lawn Care Tools',
    imageUrl: amazonImg('41H+XAMwUxS'),
  },
  'tool-fiskars-weed-puller': {
    // PAAPI-verified 2026-07-15: B0030MIHAU, in stock at $48.98.
    name: 'Fiskars Stand-Up Weed Puller Tool (39 in)',
    description: 'Four-claw stand-up puller for removing isolated taproot and rosette weeds without a selective herbicide.',
    category: 'Tool',
    affiliateUrl: amazonUrl('B0030MIHAU'),
    price: '$49',
    productsPageFilter: 'Lawn Care Tools',
    imageUrl: amazonImg('317GIA+WmJL'),
  },
  'surfactant-non-ionic': {
    name: 'Southern Ag Surfactant for Herbicides Non-Ionic, 16oz, 1 Pint',
    description: 'Surfactant for foliar herbicide applications that need better leaf coverage and uptake.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B004XDHRCE'),
    price: '$14',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('51fBawyDg7L'),
  },
  // Cool-season seed picks: enthusiast-grade named cultivars / certified blends
  // (Outsidepride cultivar blends, Jonathan Green Black Beauty, Jacklin/Barenbrug),
  // full-lawn bag sizes. Key names kept (legacy) to avoid touching override refs.
  'seed-nightshift-ryegrass': {
    name: 'Outsidepride Fireball & Hattrick Perennial Ryegrass Blend (5 lb)',
    description: 'Elite 50/50 blend of Fireball and Hattrick perennial ryegrass cultivars — dark, fine-bladed, wear-tolerant turf; 5 lb full-lawn bag.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0040B6SO2'),
    price: '$30',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('61JMOwnTsuL'),
  },
  'seed-nightshift-fescue': {
    name: 'Jonathan Green Black Beauty Ultra Grass Seed (7 lb)',
    description: 'The enthusiast-favorite turf-type tall fescue blend with deep roots, dark color, and drought tolerance; 7 lb full-lawn bag.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B003AYWIU8'),
    price: '$38',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('71I5+E4pKxL'),
  },
  'seed-nightshift-kbg': {
    name: 'Jacklin Seed by Barenbrug Heisman Kentucky Bluegrass Blend (5 lb)',
    description: 'Certified 85% Kentucky bluegrass / 15% perennial ryegrass blend from a pro sod-grower brand — dense, self-repairing KBG turf; 5 lb full-lawn bag.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B07SGL71PP'),
    price: '$38',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41bn4mXqXVL'),
  },
  'soil-humic-acid': {
    name: 'GS Plant Foods Liquid Humic Acid',
    description: 'Leonardite-derived humic acid that improves soil structure and nutrient availability. OMRI listed, 1 gallon.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B00CSAOFUG'),
    price: '$42',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('711d-nF1uML'),
  },
  'soil-sea-kelp': {
    name: 'GS Plant Foods Liquid Kelp Fertilizer',
    description: 'OMRI-listed seaweed concentrate for root development and stress recovery. 1 gallon makes 800+ gallons.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B089HSJQ4Q'),
    price: '$40',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('71F62TboR3L'),
  },
  'summer-hydretain': {
    name: 'Hydretain Root Zone Moisture Manager',
    description: 'Converts humidity into plant-usable water in the root zone. Reduces watering needs by up to 50% in summer heat.',
    category: 'Watering',
    affiliateUrl: amazonUrl('B099FJY4YT'),
    price: '$123',
    productsPageFilter: 'Watering & Irrigation',
    imageUrl: amazonImg('81hzVv9xLLL'),
  },
  'summer-wetting-agent': {
    name: 'GS Plant Foods Kelp + Yucca Wetting Agent',
    description: 'Natural wetting agent that improves water penetration and heat/drought stress tolerance for lawns.',
    category: 'Watering',
    affiliateUrl: amazonUrl('B0BSLTXHQ3'),
    price: '$43',
    productsPageFilter: 'Watering & Irrigation',
    imageUrl: amazonImg('71YEgPiL+7L'),
  },
  'smart-rachio': {
    name: 'Rachio 3 Smart Sprinkler Controller 8-Zone',
    description: 'WiFi sprinkler controller with weather-adaptive scheduling that saves 20-50% on water. Works with Alexa, Google, and HomeKit.',
    category: 'Smart Tech',
    affiliateUrl: amazonUrl('B07CZ864Y9'),
    price: '$159',
    productsPageFilter: 'Smart Lawn Tech & Automation',
    imageUrl: amazonImg('71asdFrVrlL'),
  },
  'smart-soil-sensor': {
    name: 'RAINPOINT 3-in-1 WiFi Soil Moisture Sensor',
    description: 'Wireless soil sensor that monitors moisture, temperature, and light with phone alerts. Know exactly when your lawn needs water.',
    category: 'Smart Tech',
    affiliateUrl: amazonUrl('B0D9PW8Q2H'),
    price: '$33',
    productsPageFilter: 'Smart Lawn Tech & Automation',
    imageUrl: amazonImg('61SxfjfDGlL'),
  },
  // --- /compare brand-page products (added 2026-06-30). PAAPI-verified titles/prices;
  // prices auto-refresh via `npm run paapi:refresh:files`. Weed & Feed / Triple Action
  // also appear in data/article-product-picks.ts + data/affiliate-product-catalog.ts;
  // this catalog is the single source for compare pages (consolidate later if desired).
  'weed-feed-scotts': {
    name: 'Scotts Turf Builder Weed & Feed',
    description: 'Combination broadleaf weed killer and lawn fertilizer in one granular application for established lawns with a light-to-moderate weed problem.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B00XJM8KMQ'),
    price: '$65',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('41IVt6FtH2L'),
  },
  'weed-triple-action-scotts': {
    name: 'Scotts Turf Builder Triple Action',
    description: 'Three-in-one crabgrass preventer, broadleaf weed killer, and fertilizer for a single spring application that handles prevention and feeding together.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B09JVFT7N5'),
    price: '$38',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('51in7bV-LRL'),
  },
  'post-emergent-quinclorac': {
    name: 'BASF Drive XLR8 Crabgrass Killer (Quinclorac)',
    description: 'Water-based quinclorac concentrate for post-emergent control of crabgrass and listed broadleaf weeds when they have already broken through.',
    category: 'Herbicide',
    affiliateUrl: amazonUrl('B0058W42QS'),
    price: '$55',
    productsPageFilter: 'Weed Control',
    imageUrl: amazonImg('41sfNjwp+0L'),
  },
  'seed-scotts-sun-shade': {
    name: 'Scotts Turf Builder Grass Seed Sun and Shade Mix',
    description: 'Retail-tier coated seed blend for a variety of sun/shade conditions when you want a widely available, homeowner-friendly grass seed.',
    category: 'Seed',
    affiliateUrl: amazonUrl('B0B72GKJM5'),
    price: '$30',
    productsPageFilter: 'Grass Seed & Lawn Repair',
    imageUrl: amazonImg('41mYHRy5mCL'),
  },
  'fertilizer-scotts-turf-builder': {
    name: 'Scotts Turf Builder Lawn Food',
    description: 'All-grass-type granular lawn food, the retail-tier consumer counterpart to a commercial-grade fertilizer program.',
    category: 'Fertilizer',
    affiliateUrl: amazonUrl('B005W1QXQW'),
    price: '$22',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41-eKeqethL'),
  },
  'soil-peat-moss': {
    name: 'Espoma Organic Peat Moss',
    description: 'All-natural sphagnum peat moss soil amendment that improves aeration and moisture retention; acidic, best for holding water in amendments and seedbeds.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B0037AP20I'),
    price: '$15',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('412z42Lu-nL'),
  },
  'soil-topsoil': {
    name: 'Michigan Peat Garden Magic Organic Top Soil',
    description: 'Screened, ready-to-use bagged topsoil blend for filling, leveling, and light grading where you need mineral soil rather than an acidic amendment.',
    category: 'Soil Amendment',
    affiliateUrl: amazonUrl('B000GQ4KX6'),
    price: '$29',
    productsPageFilter: 'Fertilizers & Soil Health',
    imageUrl: amazonImg('41yhbEw7NyL'),
  },
}

type SectionProductMap = Record<string, ProductKey[]>

const DEFAULT_SECTION_PRODUCTS: SectionProductMap = {
  'soil-ph': ['soil-lime-pennington', 'soil-humic-acid'],
  fertilizer: ['fertilizer-pgf-complete', 'fertilizer-simple-1648'],
  watering: ['smart-rachio', 'summer-hydretain'],
  'weed-control': ['pre-emergent-scotts-halts', 'post-emergent-tzone'],
  'pests-disease': ['grub-control-grubex', 'pest-sevin'],
  'aeration-dethatching': ['smart-soil-sensor'],
}

const GUIDE_SECTION_PRODUCT_OVERRIDES: Record<string, Partial<SectionProductMap>> = {
  'kentucky-bluegrass': {
    fertilizer: ['fertilizer-pgf-complete', 'fertilizer-simple-1648'],
    seeding: ['seed-nightshift-kbg'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['post-emergent-tenacity', 'post-emergent-tzone'],
  },
  'tall-fescue': {
    fertilizer: ['fertilizer-pgf-complete', 'fertilizer-simple-1648'],
    seeding: ['seed-nightshift-fescue'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['post-emergent-tenacity', 'post-emergent-tzone'],
  },
  'fine-fescue': {
    fertilizer: ['fertilizer-simple-1648', 'fertilizer-pgf-complete'],
    seeding: ['seed-fescue-jonathan-green'],
    'weed-control': ['post-emergent-tenacity', 'pre-emergent-scotts-halts'],
  },
  'perennial-ryegrass': {
    fertilizer: ['fertilizer-pgf-complete', 'fertilizer-simple-1648'],
    seeding: ['seed-nightshift-ryegrass'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['post-emergent-tenacity', 'post-emergent-tzone'],
  },
  'annual-ryegrass': {
    fertilizer: ['fertilizer-simple-1648', 'fertilizer-liquid-nitrogen'],
    seeding: ['seed-annual-ryegrass'],
    watering: ['smart-rachio'],
    // Pre-emergents would block the annual overseed from re-establishing each
    // fall, so this list is post-emergent broadleaf only.
    'weed-control': ['post-emergent-tzone', 'post-emergent-speedzone'],
  },
  'mixed-cool-season-blend': {
    fertilizer: ['fertilizer-pgf-complete', 'fertilizer-simple-1648'],
    // Pennington's sun & shade mix IS a mixed cool-season blend.
    seeding: ['seed-cool-pennington'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['post-emergent-tenacity', 'post-emergent-tzone'],
  },
  kikuyugrass: {
    fertilizer: ['fertilizer-pgf-complete', 'fertilizer-ammonium-sulfate'],
    // Kikuyu establishes from sod/stolons; no seed or plug product to recommend.
    watering: ['smart-rachio', 'summer-hydretain'],
    // Mirror Bermuda's list — only Bermuda-safe products are label-verified for kikuyu.
    'weed-control': ['pre-emergent-scotts-halts', 'post-emergent-trimec'],
  },
  bermudagrass: {
    // 2026-07-11 affiliate de-risk: bermuda's high-N demand leads with ammonium
    // sulfate; PGF kept as the balanced #2 (was #1 across every warm-season pillar).
    fertilizer: ['fertilizer-ammonium-sulfate', 'fertilizer-pgf-complete'],
    seeding: ['seed-bermuda'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['pre-emergent-scotts-halts', 'post-emergent-trimec'],
  },
  zoysiagrass: {
    // 2026-07-11 affiliate de-risk: Southern-formulated granular leads; PGF #2.
    fertilizer: ['fertilizer-southern-lawn-food', 'fertilizer-pgf-complete'],
    // Zoysia-from-seed is a $200 specialty product with poor conversion, so we don't
    // recommend seed. The section ("Seeding & Establishment") points at plugs instead —
    // the credible, affordable way to actually start a zoysia lawn.
    seeding: ['establish-zoysia-plugs'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['pre-emergent-dimension', 'post-emergent-image-southern'],
  },
  'st-augustinegrass': {
    'soil-ph': ['soil-humic-acid', 'soil-sea-kelp'],
    // 2026-07-11 affiliate de-risk: Southern-formulated granular leads; PGF #2.
    fertilizer: ['fertilizer-southern-lawn-food', 'fertilizer-pgf-complete', 'iron-petratools'],
    // St. Augustine has no viable seed — it's established from sod or plugs. The
    // "Establishing St. Augustine" section points at plugs.
    seeding: ['establish-st-augustine-plugs'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['pre-emergent-dimension', 'post-emergent-image-southern'],
  },
  centipede: {
    'soil-ph': ['soil-magical-alkaline', 'soil-humic-acid'],
    fertilizer: ['fertilizer-milorganite', 'iron-petratools'],
    seeding: ['seed-centipede-pennington'],
    'weed-control': ['pre-emergent-scotts-halts', 'post-emergent-image-southern'],
  },
  bahiagrass: {
    // 2026-07-11 affiliate de-risk: Southern-formulated granular leads; PGF #2.
    fertilizer: ['fertilizer-southern-lawn-food', 'fertilizer-pgf-complete'],
    seeding: ['seed-bahia-pennington'],
    watering: ['smart-rachio', 'summer-hydretain'],
    'weed-control': ['pre-emergent-barricade', 'post-emergent-image-southern'],
  },
  buffalograss: {
    fertilizer: ['fertilizer-milorganite', 'fertilizer-pgf-complete'],
    // Buffalo-from-seed is a ~$190 niche specialty product removed for conversion; the
    // "Seeding & Establishment" section points at plugs (UC Verde) instead.
    seeding: ['establish-buffalo-plugs'],
    'weed-control': ['pre-emergent-barricade', 'post-emergent-image-southern'],
  },
}

// Which sections get inline product cards per guide. Targets the 4 high-intent
// purchase decisions in lawn care: soil testing/amendments, fertilizer, seeding,
// and weed control. Watering is intentionally excluded — smart controllers are a
// considered purchase that doesn't convert from guide pages.
const PRIORITY_SECTIONS = ['soil-ph', 'fertilizer', 'seeding', 'weed-control']

const GUIDE_PRIORITY_SECTIONS: Record<string, string[]> = {
  'kentucky-bluegrass': PRIORITY_SECTIONS,
  'tall-fescue': PRIORITY_SECTIONS,
  'fine-fescue': PRIORITY_SECTIONS,
  'perennial-ryegrass': PRIORITY_SECTIONS,
  'annual-ryegrass': PRIORITY_SECTIONS,
  'mixed-cool-season-blend': PRIORITY_SECTIONS,
  kikuyugrass: PRIORITY_SECTIONS,
  bermudagrass: PRIORITY_SECTIONS,
  zoysiagrass: PRIORITY_SECTIONS,
  'st-augustinegrass': PRIORITY_SECTIONS,
  centipede: PRIORITY_SECTIONS,
  bahiagrass: PRIORITY_SECTIONS,
  buffalograss: PRIORITY_SECTIONS,
}

const DEFAULT_PRIORITY_SECTIONS = PRIORITY_SECTIONS

export function getTopSectionsForGuide(guideSlug: string): string[] {
  return GUIDE_PRIORITY_SECTIONS[guideSlug] ?? DEFAULT_PRIORITY_SECTIONS
}

/**
 * The catalog keys mapped to a guide section, pre-hydration. Compare pages need
 * the typed ProductKey (not the hydrated ProductRecommendation) so they can feed
 * linkFromCatalog and keep a single hydration path. getProductsForSection hydrates
 * on top of this.
 */
export function getProductKeysForSection(
  guideSlug: string,
  sectionId: string
): ProductKey[] {
  const guideOverrides = GUIDE_SECTION_PRODUCT_OVERRIDES[guideSlug] || {}
  return guideOverrides[sectionId] ?? DEFAULT_SECTION_PRODUCTS[sectionId] ?? []
}

/**
 * Establishment (plug) products for grasses the site steers away from seed: St. Augustine
 * (no viable seed — sod/plug only), zoysia and buffalo (slow, costly specialty seed). These
 * are the products the guides' "Seeding & Establishment" sections and the /compare sides
 * point at instead of seed. Kept as an explicit set so callers can label them "plugs" rather
 * than "seed" (they live in the `seeding` section, so a section check can't tell them apart).
 */
export const ESTABLISHMENT_PLUG_KEYS: ReadonlySet<ProductKey> = new Set<ProductKey>([
  'establish-zoysia-plugs',
  'establish-st-augustine-plugs',
  'establish-buffalo-plugs',
])

export function isEstablishmentPlug(key: ProductKey): boolean {
  return ESTABLISHMENT_PLUG_KEYS.has(key)
}

/**
 * The single grass product a /compare side should show: the grass's establishment pick
 * (seed for seeded grasses, plugs for sod/plug grasses) from its `seeding` section, else null.
 */
export function getCompareGrassProductKey(guideSlug: string): ProductKey | null {
  return getProductKeysForSection(guideSlug, 'seeding')[0] ?? null
}

export function getProductsForSection(
  guideSlug: string,
  sectionId: string
): ProductRecommendation[] {
  return getProductKeysForSection(guideSlug, sectionId)
    .map((key) => PRODUCTS[key])
    .filter(Boolean)
}

export function getAllGuideProducts(): ProductRecommendation[] {
  return Object.values(PRODUCTS)
}

/**
 * Resolve a single catalog product by its typed key. Returns undefined for
 * unknown keys so callers can fall back gracefully. Used by feature surfaces
 * (e.g., soil-test analyzer) that map domain-specific actions to catalog SKUs.
 */
export function getProductByKey(key: ProductKey): ProductRecommendation | undefined {
  return PRODUCTS[key]
}

/**
 * Returns 2-3 "top pick" products for a guide's sidebar.
 * Pulls the first fertilizer and seeding product for the guide,
 * falling back to defaults.
 */
export function getSidebarProducts(guideSlug: string): ProductRecommendation[] {
  const overrides = GUIDE_SECTION_PRODUCT_OVERRIDES[guideSlug] || {}

  const fertKeys = overrides['fertilizer'] ?? DEFAULT_SECTION_PRODUCTS['fertilizer'] ?? []
  const seedKeys = overrides['seeding'] ?? []
  const weedKeys = overrides['weed-control'] ?? DEFAULT_SECTION_PRODUCTS['weed-control'] ?? []

  // Take first fertilizer, first seed (if any), first weed control
  const picks: ProductKey[] = []
  if (fertKeys[0]) picks.push(fertKeys[0])
  if (seedKeys[0]) picks.push(seedKeys[0])
  if (weedKeys[0] && picks.length < 3) picks.push(weedKeys[0])

  // Guides without a seeding pick (no viable seed product) would stop at 2 cards;
  // pad to 3 from the guide's remaining fertilizer/weed-control keys.
  for (const key of [...fertKeys, ...weedKeys]) {
    if (picks.length >= 3) break
    if (!picks.includes(key)) picks.push(key)
  }

  return picks
    .map((key) => PRODUCTS[key])
    .filter(Boolean)
}
